import { isEmpty, split, map, parseInt, toLower } from "lodash";
import { splitCommaSeparatedStringToArray } from "../../../../../../utils";

const customAdunitValidationFields = [
  { field: "ad_formats", type: "adformats" },
  { field: "aspect_ratios", type: "aspectRatios" },
  { field: "asset.aspect_ratio", type: "cross" },
  { field: "asset.capability.audio", type: "boolean" },
  { field: "asset.capability.banner", type: "boolean" },
  { field: "asset.capability.video", type: "boolean" },
  { field: "asset.capability.perview", type: "boolean" },
  { field: "asset.image_url", type: "textLength", limit: 1000 },
  { field: "asset.mimes", type: "list" },
  { field: "asset.name", type: "countLength", limit: 100 },
  { field: "asset.screen_count", type: "range", min: 1, max: 2147483647 },
  { field: "asset.size", type: "range", min: 5, max: 2147483647 },
  { field: "auction.alt_bidfloors.banner", type: "countLength", limit: 6 },
  {
    field: "auction.alt_bidfloors.banner",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "auction.alt_bidfloors.video", type: "countLength", limit: 6 },
  {
    field: "auction.alt_bidfloors.video",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "auction.bidfloor", type: "countLength", limit: 6 },
  { field: "auction.bidfloor", type: "digitsBeforeDecimal", limit: 4 },
  { field: "created_by", type: "uuid" },
  { field: "eids", type: "eids" },
  { field: "id", type: "uuid" },
  { field: "keywords", type: "entityName", entityLengthCount: 35 },
  { field: "location.altitude", type: "countLength", limit: 6 },
  { field: "location.bearing", type: "countLength", limit: 5 },
  {
    field: "location.bearing",
    type: "range",
    min: 0,
    max: 360,
  },
  { field: "location.city", type: "textLength", limit: 100 },
  { field: "location.country", type: "countLength", limit: 100 },
  {
    field: "location.dma_code",
    type: "range",
    min: 1,
    max: 999,
  },
  {
    field: "location.horizontal_accuracy",
    type: "digitsBeforeDecimal",
    limit: 3,
  },
  { field: "location.horizontal_accuracy", type: "countLength", limit: 9 },
  {
    field: "location.lat",
    type: "range",
    min: -90,
    max: 90,
  },
  {
    field: "location.lon",
    type: "range",
    min: -180,
    max: 180,
  },
  { field: "location.method", type: "textLength", limit: 100 },
  { field: "location.region", type: "textLength", limit: 100 },
  { field: "location.speed", type: "countLength", limit: 8 },
  { field: "location.vertical_accuracy", type: "countLength", limit: 9 },
  {
    field: "location.vertical_accuracy",
    type: "digitsBeforeDecimal",
    limit: 3,
  },
  { field: "location.zip", type: "textLength", limit: 10 },
  { field: "measurement.duration", type: "countLength", limit: 6 },
  { field: "measurement.segment", type: "textLength", limit: 100 },
  {
    field: "measurement_config.ads_per_hour",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "name", type: "countLength", limit: 100 },
  { field: "network_id", type: "uuid" },
  // Changing valiation for min and max length insted of limit.
  { field: "notes", type: "textRange", minLength: 2, maxLength: 4000 },
  { field: "owned_by", type: "uuid" },
  { field: "planning.base_rate", type: "countLength", limit: 6 },
  { field: "planning.base_rate", type: "digitsBeforeDecimal", limit: 4 },
  { field: "restrictions.battr", type: "list" },
  { field: "restrictions.bcat", type: "list" },
  { field: "restrictions.wlang", type: "list" },
  { field: "restrictions.btype", type: "list" },
  { field: "restrictions.badomain", type: "listField" },
  { field: "restrictions.bbundle", type: "listField" },
  { field: "restrictions.bbuyer", type: "listField" },
  { field: "restrictions.bcid", type: "listField" },
  { field: "restrictions.bcrid", type: "listField" },
  { field: "restrictions.bseat", type: "listField" },
  { field: "start_date", type: "cross" },
  { field: "start_date", type: "date" },
  { field: "start_date", type: "int32-max-date-compare" },
  // Adding validation for Network ID and Network Name mismatch.
  { field: "network_name", type: "network_exist" },
  /** type: 'required_with_col'
   * Added validation on status column
   * start_date column should be present in CSV if status is "1"
   */
  {
    field: "status",
    type: "required_column",
    params: { colName: "start_date", if_val: "1" },
  },
  { field: "slot.h", type: "isGreaterThan", limit: 2147483647 },
  { field: "slot.max_duration", type: "digitsBeforeDecimal", limit: 4 },
  { field: "slot.min_duration", type: "digitsBeforeDecimal", limit: 4 },
  { field: "slot.max_duration", type: "crossMinMax" },
  { field: "slot.w", type: "isGreaterThan", limit: 2147483647 },
  { field: "venue.address", type: "textLength", limit: 200 },
  { field: "venue.name", type: "textLength", limit: 100 },
];

const customNetworkValidationFields = [
  { field: "auction.alt_bidfloors.banner", type: "countLength", limit: 6 },
  {
    field: "auction.alt_bidfloors.banner",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "auction.alt_bidfloors.video", type: "countLength", limit: 6 },
  {
    field: "auction.alt_bidfloors.video",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "branding.bundle", type: "textLength", limit: 100 },
  { field: "branding.shortcode", type: "textLength", limit: 100 },
  { field: "branding.shorturl", type: "textLength", limit: 200 },
  { field: "branding.url", type: "textLength", limit: 200 },
  { field: "measurement.duration", type: "countLength", limit: 6 },
  { field: "measurement.segment", type: "textLength", limit: 100 },
  {
    field: "measurement_config.ads_per_hour",
    type: "digitsBeforeDecimal",
    limit: 4,
  },
  { field: "name", type: "countLength", limit: 100 },
  { field: "notes", type: "textLength", limit: 4000 },
  { field: "owned_by", type: "uuid" },
  { field: "restrictions.battr", type: "list" },
  { field: "restrictions.bcat", type: "list" },
  { field: "restrictions.wlang", type: "list" },
  { field: "restrictions.btype", type: "list" },
  { field: "restrictions.badomain", type: "listField" },
  { field: "restrictions.bbundle", type: "listField" },
  { field: "restrictions.bbuyer", type: "listField" },
  { field: "restrictions.bcid", type: "listField" },
  { field: "restrictions.bcrid", type: "listField" },
  { field: "restrictions.bseat", type: "listField" },
];

const listFields = [
  "asset.mimes",
  "restrictions.badomain",
  "restrictions.battr",
  "restrictions.bbundle",
  "restrictions.bbuyer",
  "restrictions.bcat",
  "restrictions.bcid",
  "restrictions.bcrid",
  "restrictions.bseat",
  "restrictions.btype",
  "restrictions.wlang",
];

const commaSeparatedStringToJson = (
  commaSeparatedString,
  indexDelimiter,
  fieldName
) => {
  //add_formats fild changing to lower case so that "X" trqansform to "x".
  commaSeparatedString = toLower(commaSeparatedString);
  let objList = [];
  if (!isEmpty(commaSeparatedString)) {
    objList = map(split(commaSeparatedString, ","), (element) => {
      let jsonObj = {};
      const index = element.indexOf(indexDelimiter);
      if (fieldName === "ad_formats") {
        jsonObj = {
          w: parseInt(element.substr(0, index)),
          h: parseInt(element.substr(index + 1)),
        };
      } else if (fieldName === "aspect_ratios") {
        jsonObj = {
          wratio: parseInt(element.substr(0, index)),
          hratio: parseInt(element.substr(index + 1)),
        };
      }
      return jsonObj;
    });
  }
  return objList;
};

const postProcessFields = listFields.map((item) => ({
  key: item,
  process: (value) => splitCommaSeparatedStringToArray(value) || [],
}));

postProcessFields.push(
  {
    key: "ad_formats",
    process: (value) => commaSeparatedStringToJson(value, "x", "ad_formats"),
  },
  {
    key: "aspect_ratios",
    process: (value) => commaSeparatedStringToJson(value, ":", "aspect_ratios"),
  }
);

export {
  customAdunitValidationFields,
  customNetworkValidationFields,
  postProcessFields,
};
